/**
 * Index arithmetic for a stack that may be showing only some of its items (a filter, or inactive
 * offers hidden). The drag library reports positions in the visible list; these turn them into
 * positions in the whole list, so a drop lands next to the rows the user saw and the hidden ones
 * keep their place.
 */

/** The full-list index to insert at so the item lands before the visible row at `visibleIndex`. */
function insertionIndex<T>(items: T[], visibleKeys: string[], getKey: (item: T) => string, visibleIndex: number) {
  const target = visibleKeys[visibleIndex];
  if (target !== undefined) return items.findIndex((item) => getKey(item) === target);
  // Past the last visible row: right after it, or at the end when nothing is visible.
  const last = visibleKeys[visibleKeys.length - 1];
  return last === undefined ? items.length : items.findIndex((item) => getKey(item) === last) + 1;
}

/** Moves the visible row at `from` so it ends up at visible position `to` (the drag library's destination index). */
export function moveVisible<T>(
  items: T[],
  visibleKeys: string[],
  getKey: (item: T) => string,
  from: number,
  to: number
) {
  const movedKey = visibleKeys[from];
  if (movedKey === undefined || from === to) return items;
  const remaining = items.filter((item) => getKey(item) !== movedKey);
  const moved = items.find((item) => getKey(item) === movedKey) as T;
  const at = insertionIndex(
    remaining,
    visibleKeys.filter((key) => key !== movedKey),
    getKey,
    to
  );
  return [...remaining.slice(0, at), moved, ...remaining.slice(at)];
}

/** Inserts a new item so it shows at visible position `to`. */
export function insertVisible<T>(items: T[], visibleKeys: string[], getKey: (item: T) => string, item: T, to: number) {
  const at = insertionIndex(items, visibleKeys, getKey, to);
  return [...items.slice(0, at), item, ...items.slice(at)];
}

export function moveToEdge<T>(items: T[], index: number, edge: 'top' | 'bottom') {
  const item = items[index];
  if (item === undefined) return items;
  const rest = items.filter((_, i) => i !== index);
  return edge === 'top' ? [item, ...rest] : [...rest, item];
}

export const LIBRARY_DROPPABLE_ID = 'stack-builder-library';
export const STACK_DROPPABLE_ID = 'stack-builder-stack';

/** Moves a row one visible place up (-1) or down (+1), stepping over hidden rows; unchanged at either end. */
export function moveStep<T>(
  items: T[],
  visibleKeys: string[],
  getKey: (item: T) => string,
  key: string,
  delta: -1 | 1
) {
  const from = visibleKeys.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= visibleKeys.length) return items;
  return moveVisible(items, visibleKeys, getKey, from, to);
}
